import type {
  Entry,
  EntriesResponse,
  UploadMetadata,
  UploadResponse,
  SearchResponse,
  ErrorResponse,
  PresignedUploadRequest,
  PresignedUploadResponse,
  ConfirmUploadRequest,
  SharedAccountsResponse,
  OutgoingSharesResponse,
  CreateShareResponse,
} from '../types/api';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787'}/api`;

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public error: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle unauthorized - redirect to login
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      throw new ApiError('Unauthorized', 401, 'Unauthorized');
    }

    const errorData: ErrorResponse = await response.json().catch(() => ({
      error: 'Unknown Error',
      message: 'An unexpected error occurred',
    }));

    throw new ApiError(
      errorData.message,
      response.status,
      errorData.error
    );
  }

  return response.json();
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

export const api = {
  /**
   * Upload audio recording (legacy method for small files)
   */
  async uploadAudio(blob: Blob, metadata: UploadMetadata): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('audio', blob, 'recording.webm');
    formData.append('metadata', JSON.stringify(metadata));

    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
      credentials: 'include',
    });

    return handleResponse<UploadResponse>(response);
  },

  /**
   * Request presigned upload URL for large files
   */
  async requestUploadUrl(request: PresignedUploadRequest): Promise<PresignedUploadResponse> {
    const response = await fetch(`${API_BASE}/entries/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(request),
      credentials: 'include',
    });

    return handleResponse<PresignedUploadResponse>(response);
  },

  /**
   * Upload file directly to R2 using presigned URL
   * Note: Presigned URLs contain authentication in query params, no auth headers needed
   */
  async uploadDirect(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = (e.loaded / e.total) * 100;
          onProgress(percent);
        }
      });

      // Handle successful upload
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(
            new ApiError(
              'Failed to upload file',
              xhr.status,
              'Upload Error'
            )
          );
        }
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        reject(new ApiError('Network error during upload', 0, 'Network Error'));
      });

      // Handle aborted uploads
      xhr.addEventListener('abort', () => {
        reject(new ApiError('Upload aborted', 0, 'Upload Aborted'));
      });

      // Start upload
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  /**
   * Confirm upload completion and trigger transcription
   */
  async confirmUpload(request: ConfirmUploadRequest): Promise<UploadResponse> {
    const response = await fetch(`${API_BASE}/entries/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(request),
      credentials: 'include',
    });

    return handleResponse<UploadResponse>(response);
  },

  /**
   * Get all entries for authenticated user or shared account
   */
  async getEntries(
    accountId?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<EntriesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (accountId) {
      params.append('accountId', accountId);
    }

    const response = await fetch(`${API_BASE}/entries?${params}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<EntriesResponse>(response);
  },

  /**
   * Get single entry by ID
   */
  async getEntry(id: string): Promise<Entry> {
    const response = await fetch(`${API_BASE}/entries/${id}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<Entry>(response);
  },

  /**
   * Update entry title
   */
  async updateEntry(id: string, title: string): Promise<void> {
    const response = await fetch(`${API_BASE}/entries/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ title }),
      credentials: 'include',
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Delete entry
   */
  async deleteEntry(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/entries/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Search entries for authenticated user
   */
  async searchEntries(
    query: string,
    dateFrom?: number,
    dateTo?: number,
    limit: number = 50
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });

    if (dateFrom) {
      params.append('dateFrom', dateFrom.toString());
    }

    if (dateTo) {
      params.append('dateTo', dateTo.toString());
    }

    const response = await fetch(`${API_BASE}/search?${params}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<SearchResponse>(response);
  },

  /**
   * Get Cloudflare Stream upload URL
   */
  async getStreamUploadUrl(
    duration: number,
    videoFlipped: boolean = false
  ): Promise<{ entryId: string; uploadURL: string; streamUid: string }> {
    const response = await fetch(`${API_BASE}/stream/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ duration, videoFlipped }),
      credentials: 'include',
    });

    return handleResponse<{ entryId: string; uploadURL: string; streamUid: string }>(response);
  },

  /**
   * Complete Cloudflare Stream upload
   */
  async completeStreamUpload(entryId: string, duration: number): Promise<void> {
    const response = await fetch(`${API_BASE}/stream/${entryId}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ duration }),
      credentials: 'include',
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Retry transcription for a failed entry
   */
  async retranscribeEntry(id: string): Promise<{ success: boolean; message: string }> {
    // Force sync mode in dev to bypass queue issues
    const isDev = API_BASE.includes('localhost') || API_BASE.includes('127.0.0.1') || API_BASE.includes('8787');
    const url = `${API_BASE}/entries/${id}/retranscribe${isDev ? '?sync=true' : ''}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<{ success: boolean; message: string }>(response);
  },

  /**
   * Get accounts shared with the authenticated user
   */
  async getSharedAccounts(): Promise<SharedAccountsResponse> {
    const response = await fetch(`${API_BASE}/shared-accounts`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<SharedAccountsResponse>(response);
  },

  /**
   * Get shares the authenticated user has created (outgoing shares)
   */
  async getOutgoingShares(): Promise<OutgoingSharesResponse> {
    const response = await fetch(`${API_BASE}/shares`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<OutgoingSharesResponse>(response);
  },

  /**
   * Create a share by email
   */
  async createShare(email: string): Promise<CreateShareResponse> {
    const response = await fetch(`${API_BASE}/shares`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });

    return handleResponse<CreateShareResponse>(response);
  },

  /**
   * Delete a share
   */
  async deleteShare(shareId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/shares/${shareId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    return handleResponse<{ success: boolean; message: string }>(response);
  },
};

export { ApiError };
